import { useEffect, useId, useRef } from 'react';

// Joint coordinates belong to the original renders. All layers share one viewBox,
// so the cutouts and eye lights stay registered at every responsive size.
const POSES = {
  welcome: {
    src: 'mako-welcome.webp', width: 1000, height: 1500,
    head: 'M410 0H680V210L590 209L470 192L410 170Z', pivot: [548, 205],
    limb: 'M0 370H275L332 411Q369 426 362 467L356 540H0Z', joint: [329, 446],
    eyes: [[518, 112], [591, 122]], eyeWidth: 18, eyeAngle: 8,
    label: 'Mako begrüßen',
  },
  guide: {
    src: 'mako-guide.webp', width: 1000, height: 1000,
    head: 'M210 0H520V265L433 271L286 222L210 190Z', pivot: [350, 251],
    limb: 'M1000 320V660H620Q550 603 568 550L616 501L750 390Z', joint: [602, 559],
    eyes: [[389, 168], [454, 187]], eyeWidth: 18, eyeAngle: 17,
    label: 'Mako einen Hinweis geben lassen',
  },
  closeup: {
    src: 'mako-closeup-cutout.webp', width: 1448, height: 1086,
    head: 'M500 0H1150V510L1050 544L750 520L696 481L703 448L645 438L606 465L510 430Z', pivot: [818, 520],
    limb: '', joint: [0, 0], eyes: [[668, 271], [881, 325]], eyeWidth: 44, eyeAngle: 15,
    label: 'Mako zum Nicken bringen',
  },
  done: {
    src: 'mako-done.webp', width: 1000, height: 1000,
    head: 'M416 0H625V180L560 183L448 154L416 135Z', pivot: [514, 173],
    limb: 'M433 539Q494 511 550 551L571 730L628 1000H433Z', joint: [502, 555],
    eyes: [[498, 100], [553, 117]], eyeWidth: 14, eyeAngle: 17,
    label: 'Mit Mako feiern',
  },
} as const;

type Pose = keyof typeof POSES;
const clamp = (n: number) => Math.max(0, Math.min(1, n));

export function LandingMako({ pose, celebrate = false }: { pose: Pose; celebrate?: boolean | number }) {
  const config = POSES[pose];
  const id = useId().replace(/:/g, '');
  const root = useRef<HTMLElement | null>(null);
  const head = useRef<SVGGElement>(null);
  const limb = useRef<SVGGElement>(null);
  const eyes = useRef<SVGGElement>(null);
  const reaction = useRef<(() => void) | null>(null);

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0, pulseStart = -Infinity, pointerX = 0, pointerY = 0;
    const draw = (now: number) => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const progress = pose === 'welcome'
        ? clamp((innerHeight * .85 - rect.top) / (innerHeight * .75 + rect.height * .85))
        : clamp((innerHeight * .92 - rect.top) / (innerHeight * .72 + rect.height * .25));
      const p = reduced.matches ? .5 : progress;
      // An already queued frame may carry a timestamp just before the click.
      const pulseTime = Math.max(0, (now - pulseStart) / 850);
      const pulse = !reduced.matches && pulseTime >= 0 && pulseTime < 1 ? Math.sin(pulseTime * Math.PI) : 0;
      const gesture = pose === 'done' ? 0 : Math.sin(p * Math.PI * 2);
      const nod = Math.sin(clamp((p - .28) / .58) * Math.PI * 2);
      const headAngle = reduced.matches ? 0 : (pose === 'closeup' ? nod * 2.2 : gesture * 3.5) + pointerX * 2 + pulse * 3;
      const limbAngle = reduced.matches ? 0 : pose === 'welcome' ? gesture * 9 - pulse * 8 : pose === 'guide' ? (1 - p) * 13 - pulse * 7 : gesture * 6 + pulse * 7;
      head.current?.setAttribute('transform', `translate(0 ${reduced.matches ? 0 : pointerY * 2 + pulse}) rotate(${headAngle} ${config.pivot[0]} ${config.pivot[1]})`);
      limb.current?.setAttribute('transform', `rotate(${limbAngle} ${config.joint[0]} ${config.joint[1]})`);
      // A single light swell occupies a short part of the scroll timeline.
      const flash = pose === 'done' ? 0 : Math.sin(clamp((p - .38) / .2) * Math.PI);
      eyes.current?.setAttribute('opacity', String(reduced.matches ? 0 : Math.max(flash * .85, pulse * .9)));
      element.dataset.progress = p.toFixed(3);
      if (pulseTime >= 0 && pulseTime < 1 && !reduced.matches) frame = requestAnimationFrame(draw);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(draw); };
    const react = () => { pulseStart = performance.now(); schedule(); };
    const pointer = (event: PointerEvent) => {
      if (pose === 'done' || event.pointerType !== 'mouse' || reduced.matches) return;
      const rect = element.getBoundingClientRect();
      pointerX = (clamp((event.clientX - rect.left) / rect.width) - .5) * 2;
      pointerY = (clamp((event.clientY - rect.top) / rect.height) - .5) * 2;
      schedule();
    };
    const leave = () => { pointerX = pointerY = 0; schedule(); };
    reaction.current = react;
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    reduced.addEventListener('change', schedule);
    element.addEventListener('pointermove', pointer);
    element.addEventListener('pointerleave', leave);
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      reduced.removeEventListener('change', schedule);
      element.removeEventListener('pointermove', pointer);
      element.removeEventListener('pointerleave', leave);
      observer.disconnect();
      reaction.current = null;
    };
  }, [pose, config]);

  useEffect(() => { if (celebrate) reaction.current?.(); }, [celebrate]);

  const artwork = <svg viewBox={`0 0 ${config.width} ${config.height}`} aria-hidden="true" focusable="false">
      <defs>
        <image id={`${id}-image`} href={`/landing/${config.src}`} width={config.width} height={config.height} />
        <clipPath id={`${id}-head`}><path d={config.head} /></clipPath>
        <clipPath id={`${id}-limb`}><path d={config.limb} /></clipPath>
        <mask id={`${id}-body`} maskUnits="userSpaceOnUse" x="0" y="0" width={config.width} height={config.height}>
          <rect width={config.width} height={config.height} fill="white" />
          <path d={config.head} fill="black" /><path d={config.limb} fill="black" />
        </mask>
        <filter id={`${id}-glow`} x="-100%" y="-150%" width="300%" height="400%"><feGaussianBlur stdDeviation="5" /></filter>
      </defs>
      <use href={`#${id}-image`} mask={`url(#${id}-body)`} />
      {config.limb && <g ref={limb}><use href={`#${id}-image`} clipPath={`url(#${id}-limb)`} /></g>}
      <g ref={head}>
        <use href={`#${id}-image`} clipPath={`url(#${id}-head)`} />
        <g ref={eyes} opacity="0" fill="#baffff" filter={`url(#${id}-glow)`}>
          {config.eyes.map(([x, y]) => <ellipse key={x} cx={x} cy={y} rx={config.eyeWidth} ry={config.eyeWidth * .26} transform={`rotate(${config.eyeAngle} ${x} ${y})`} />)}
        </g>
      </g>
    </svg>;
  if (pose === 'done') return <div ref={element => { root.current = element; }} className="ms-mako ms-mako-done" role="img" aria-label="Mako freut sich über erledigte Aufgaben">{artwork}</div>;
  return <button ref={element => { root.current = element; }} type="button" className={`ms-mako ms-mako-${pose}`} aria-label={config.label} onClick={() => reaction.current?.()}>{artwork}</button>;
}
